import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { FileText, Wallet, TrendingUp, TrendingDown, Calendar, AlertCircle } from 'lucide-react';
import { apiClient } from '../../utils/apiHelper';

const CashBook = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState({ total_receipts: 0, total_payments: 0, balance: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { fetchReport(); }, []);

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      let url = '/api/accounts/cash-register/transactions';
      const params = [];
      if (startDate) params.push(`from_date=${startDate}`);
      if (endDate) params.push(`to_date=${endDate}`);
      if (params.length > 0) url += '?' + params.join('&');

      const response = await apiClient.get(url);
      const data = response.data;
      
      setTransactions(data.transactions || []);
      setSummary(data.summary || { total_receipts: 0, total_payments: 0, balance: 0 });
    } catch (err) {
      console.error('Error:', err);
      setError('Failed to load cash transactions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getRunningBalance = (index) => {
    let balance = 0;
    const reversedTransactions = [...transactions].reverse();
    for (let i = 0; i <= (transactions.length - 1 - index); i++) {
      const txn = reversedTransactions[i];
      if (txn.type === 'Receipt') {
        balance += txn.amount;
      } else {
        balance -= txn.amount;
      }
    }
    return balance;
  };

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-green-600" />
            Cash Book
          </CardTitle>
          <div className="flex gap-2 items-center">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-36" />
              <span className="text-gray-500">to</span>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-36" />
            </div>
            <Button onClick={fetchReport} disabled={loading}>
              <FileText className="w-4 h-4 mr-2" />{loading ? 'Loading...' : 'Generate'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
              <Button variant="outline" size="sm" onClick={fetchReport} className="ml-auto">Retry</Button>
            </div>
          )}

          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-600" />
                <p className="text-sm text-green-600">Total Cash Receipts</p>
              </div>
              <p className="text-2xl font-bold text-green-700">₹{summary.total_receipts?.toLocaleString('en-IN')}</p>
            </div>
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <div className="flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-red-600" />
                <p className="text-sm text-red-600">Total Cash Payments</p>
              </div>
              <p className="text-2xl font-bold text-red-700">₹{summary.total_payments?.toLocaleString('en-IN')}</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2">
                <Wallet className="w-5 h-5 text-blue-600" />
                <p className="text-sm text-blue-600">Cash Balance</p>
              </div>
              <p className={`text-2xl font-bold ${summary.balance >= 0 ? 'text-blue-700' : 'text-orange-600'}`}>
                ₹{summary.balance?.toLocaleString('en-IN')}
              </p>
            </div>
          </div>

          <div className="text-sm text-gray-500 mb-2">Showing {transactions.length} cash transactions</div>

          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left p-3">Date</th>
                <th className="text-left p-3">Type</th>
                <th className="text-left p-3">Category</th>
                <th className="text-left p-3">Description</th>
                <th className="text-right p-3">Receipts (+)</th>
                <th className="text-right p-3">Payments (-)</th>
                <th className="text-right p-3">Balance</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="text-center p-8 text-gray-500">Loading...</td></tr>
              ) : transactions.length === 0 ? (
                <tr><td colSpan="7" className="text-center p-8 text-gray-500">No cash transactions found</td></tr>
              ) : (
                transactions.map((txn, idx) => (
                  <tr key={idx} className="border-b hover:bg-gray-50">
                    <td className="p-3">{txn.date}</td>
                    <td className="p-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${txn.type === 'Receipt' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {txn.type}
                      </span>
                    </td>
                    <td className="p-3">{txn.category}</td>
                    <td className="p-3 max-w-xs truncate">{txn.description || '-'}</td>
                    <td className="p-3 text-right text-green-600 font-medium">{txn.type === 'Receipt' ? `+₹${txn.amount?.toLocaleString('en-IN')}` : '-'}</td>
                    <td className="p-3 text-right text-red-600 font-medium">{txn.type === 'Payment' ? `-₹${txn.amount?.toLocaleString('en-IN')}` : '-'}</td>
                    <td className="p-3 text-right font-semibold">₹{getRunningBalance(idx)?.toLocaleString('en-IN')}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};

export default CashBook;
